import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { shape, func, array, bool, string } from 'prop-types';
import HelpIcon from '@material-ui/icons/Help';
import InfoIcon from '@material-ui/icons/Info';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import {
  Grid,
  Link,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core';

import RoleInfoDialogContent from '../RoleInfoDialogContent';
import { getI18n } from '../../../../utils/intl';
import {
  Footer,
  FormTextField,
  FormSelect,
  AlertDialog,
} from '../../../../components';
import styles from './styles';
import {
  ADMIN_USER_ROLES,
  ORG_USER_ROLES,
  ROLE_NAME_USER_MAP,
  ROLE_NAME_UUID_MAP,
  ROLE_ORG_ADMIN,
  ROLE_PORTAL_ADMIN,
  USER_ROLE_NAME_MAP,
  USER_TYPE_GLOBAL,
  USER_TYPE_LIST,
  USER_STATUS_REGISTRATION_INIT,
  USER_STATUS_REGISTRATION_PENDING_APPROVAL,
} from '../../../../constants';
import {
  deduceUserRoleAndAuthorizationType,
} from '../utils';

export const EditUser = (props) => {
  const {
    activeUserContext,
    classes,
    authSchemes,
    userFormObject,
    updateUserFormObject,
    saveClicked,
    currentUserRole,
    currentUserOrg,
    userDetails,
  } = props;

  const intl = getI18n(useIntl());

  const [invalidEmail, setInvalidEmail] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState('');
  const [showRoleInfo, setShowRoleInfo] = useState(false);

  const emailIsValid = (email) => /\S+@\S+\.\S{2,}/.test(email);

  const validateEmail = (value) => {
    let helperErrorText;
    if(value === '') {
      helperErrorText = intl.getI18nMessage('label.user.profile.email.mandatory');
    } else if (!emailIsValid(value)) {
      helperErrorText = intl.getI18nMessage('label.user.profile.email.format.error');
    }
    setInvalidEmail(helperErrorText);
    setEmailErrorText(helperErrorText);
    updateUserFormObject('email', value);
  }
  const TooltipIconForIdp = () => (
    <div className={classes.helpIconContainer}>
      <div>
        {intl.getI18nMessage('label.user.details.idp.dropdown.title')}
      </div>
      <Tooltip classes={classes} title={intl.getI18nMessage('label.user.details.idp.tooltip')} arrow placement="right">
        <HelpIcon className={classes.helpIcon}/>
      </Tooltip>
    </div>
  );
  const formatUserInfoLink = () =>
    (<Link underline = 'none' onClick={() => setShowRoleInfo(true)} className={classes.userRolesLink}>
      {intl.getI18nMessage('label.user.details.userrole.helptext.linktext')}
    </Link>);

  useEffect(()=> {
    if(saveClicked) {
      validateEmail(userFormObject.email);
    }
  }, [saveClicked]);

  const handleUserTypeChange = (val) => {
    updateUserFormObject('userType', val);
  };

  const handleUserRoleChange = (val) => {
    const oldAccess = userFormObject.access;
    const newAccess = [];
    if (userType === USER_TYPE_GLOBAL) {
      newAccess.push({
        roleUuid: ROLE_NAME_UUID_MAP[USER_ROLE_NAME_MAP[val]],
      });
    } else {
      forEach(oldAccess, item => {
        if (item.orgUuid !== currentUserOrg) {
          newAccess.push(item);
        } else {
          newAccess.push({
            orgUuid: currentUserOrg,
            roleUuid: ROLE_NAME_UUID_MAP[USER_ROLE_NAME_MAP[val]],
          });
        }
      });
    }
    updateUserFormObject('access', newAccess);
  };

  const { authConfigUuid } = userFormObject;

  const canChangeUserType = currentUserRole === ROLE_PORTAL_ADMIN;

  const { userType } = userFormObject;

  const showRoleChange = (currentUserRole === ROLE_ORG_ADMIN) || (userType === USER_TYPE_GLOBAL);

  const roleSelectionOptions = userType === USER_TYPE_GLOBAL ? ADMIN_USER_ROLES : ORG_USER_ROLES;

  const {
    userRole,
  } = deduceUserRoleAndAuthorizationType(userFormObject.access, currentUserOrg, userType);

  const {
    userRole: origUserRole,
  } = deduceUserRoleAndAuthorizationType(
    userDetails && userDetails.access, currentUserOrg, userType,
  );

  const canEditEmail = (authConfigUuid === 'DEFAULT')
    && (origUserRole !== ROLE_NAME_USER_MAP[ROLE_PORTAL_ADMIN]);
  const canEditNames = (authConfigUuid === 'DEFAULT')
    && (origUserRole !== ROLE_NAME_USER_MAP[ROLE_PORTAL_ADMIN]);

  const { authznType } =
    find(authSchemes, authScheme => authScheme.uuid === userFormObject.authConfigUuid) || {};
  const isAuthznManagedByIDP = (authznType === 'IDP') || ((currentUserRole === ROLE_ORG_ADMIN) && (activeUserContext.authznType === 'IDP'));

  const effectiveUserName = () => {
    if (userFormObject.authConfigUuid === 'DEFAULT') {
      if ((userFormObject.status === USER_STATUS_REGISTRATION_INIT) ||
        (userFormObject.status === USER_STATUS_REGISTRATION_PENDING_APPROVAL)) {
          return intl.getI18nMessage('label.user.no.username');
      }
      return userFormObject.username;
    }
    return userFormObject.username;
  };
  
  return (
    <div
      id="user-details-EditUser-container"
      data-apim-test="user-details-EditUser-container"
      data-layer7-test="user-details-EditUser-container"
    >
      <AlertDialog
        isOpen={showRoleInfo}
        title={intl.getI18nMessage('label.user.details.userroles.info.dialog.title')}
        description={
          <RoleInfoDialogContent isPortalAdmin={currentUserRole === ROLE_PORTAL_ADMIN} />
        }
        submitText={intl.getI18nMessage('label.user.details.userroles.info.dialog.close')}
        onClose={() => setShowRoleInfo(false)}
        onSubmit={() => setShowRoleInfo(false)}
        onCancel={() => setShowRoleInfo(false)}
        showCancel={false}
        dialogId={'proxy-status-error-dialog'}
      />
      <Grid container className={classes.marginPanelContainer}>
        <Grid item md={6} sm={12} xs={12} className={classes.paper}>
          <Typography id="user-details-title" variant="h4">
            {intl.getI18nMessage('label.details')}
          </Typography>
          {isAuthznManagedByIDP && intl.getI18nMessage('label.user.details.idp.authorization.details.help.text')}
          {currentUserRole === ROLE_PORTAL_ADMIN &&
            <FormSelect
              id="auth-config-uuid"
              data-apim-test="auth-config-uuid"
              noNoneOption
              noNativeSelect
              data={authSchemes}
              disabled
              name={(<TooltipIconForIdp />)}
              helperText={intl.getI18nMessage('label.user.details.idp.dropdown.helptext')}
              value={authConfigUuid}
              handleChange={val => updateUserFormObject('authConfigUuid', val)}
            />
          }
          {!isAuthznManagedByIDP && <FormTextField
            id="user-email"
            name={intl.getI18nMessage('label.user.details.email.title')}
            helperText={invalidEmail? emailErrorText :intl.getI18nMessage('label.user.details.textfields.helptext')}
            value={userFormObject.email}
            handleChange={val => validateEmail(val)}
            error={invalidEmail}
            disabled={!canEditEmail}
          />}
          {!isAuthznManagedByIDP && <FormTextField
            id="user-first-name"
            name={intl.getI18nMessage('label.user.details.firstname.title')}
            helperText={intl.getI18nMessage('label.user.details.textfields.helptext')}
            value={userFormObject.firstName}
            handleChange={val => updateUserFormObject('firstName', val)}
            optional
            disabled={!canEditNames}
          />}
          {!isAuthznManagedByIDP && <FormTextField
            id="user-last-name"
            name={intl.getI18nMessage('label.user.details.lastname.title')}
            helperText={intl.getI18nMessage('label.user.details.textfields.helptext')}
            value={userFormObject.lastName}
            handleChange={val => updateUserFormObject('lastName', val)}
            optional
            disabled={!canEditNames}
          />}
          {userFormObject.uuid &&
            <FormTextField
              id="user-name"
              name={intl.getI18nMessage('label.user.details.username.title')}
              helperText={intl.getI18nMessage('label.user.details.username.helptext')}
              value={effectiveUserName()}
              disabled
            />
          }
          {isAuthznManagedByIDP && <div className={classes.idpInfoMessageContainer}>
            <div>
              <InfoIcon className={classes.infoIcon}/>
            </div>
            <div className={classes.idpInfoMessage}>
              {intl.getI18nMessage('label.user.details.idp.authorization.warn.help.text')}
            </div>
          </div>}
        </Grid>
        <Grid item md={6} sm={12} xs={12} className={classes.paper}>
          <Typography id="user-roles-title" variant="h4">
            {intl.getI18nMessage('label.roles')}
          </Typography>
          {isAuthznManagedByIDP && intl.getI18nMessage('label.user.details.idp.authorization.role.help.text')}
          {canChangeUserType &&
            <FormSelect
              id="user-type"
              data-apim-test="user-type-select"
              disabled={isAuthznManagedByIDP}
              noNoneOption
              noNativeSelect
              name={intl.getI18nMessage('label.user.details.usertype.title')}
              data={USER_TYPE_LIST}
              helperText={intl.getI18nMessage('label.user.details.usertype.helptext')}
              value={userType}
              handleChange={val => handleUserTypeChange(val)}
            />
          }
          {showRoleChange &&
            <FormSelect
              id="user-role"
              data-apim-test="user-role-select"
              disabled={isAuthznManagedByIDP}
              noNoneOption
              noNativeSelect
              name={intl.getI18nMessage('label.user.details.userrole.title')}
              data={roleSelectionOptions}
              helperText={intl.getI18nMessage('label.user.details.userrole.helptext', { link: formatUserInfoLink() })}
              value={userRole}
              handleChange={val => handleUserRoleChange(val)}
            />
          }
        </Grid>
      </Grid>
      <Footer />
    </div>
  );
};

EditUser.propTypes = {
  activeUserContext: shape({}),
  authSchemes: array,
  classes: shape({}),
  currentUserOrg: string,
  currentUserRole: string,
  push: func,
  saveClicked: bool,
  updateUserFormObject: func,
  userDetails: shape({}),
  userFormObject: shape({}),
};

const mapStateToProps = () => ({});

const mapDispatchToProps = {
};

EditUser.displayName = 'EditUser';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(EditUser);
